/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  whenFormInvalid,
} from '@gh/builder-shared';
import { ActionFlow, AppRouter, FormStateSnapshot, getRouteInitData } from '@gh/common-shared';
import {
  DialogService,
  FormMode,
  NotificationService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { DrawRuleDetailsBusinessService } from '../../resources/draw-rule-details-business.service';
import {
  DrawRuleDetails,
  DrawRuleMasterInfo,
  DrawRuleStage,
  hasDrawRuleStageCpDeleted,
} from '../../resources/draw-rule-details.model';
import { DrawRuleDetailsFormState } from './draw-rule-details-form.service';
import { ID } from '@gh/common-shared-data';
import { filter$ } from '@gh/rx/operators';

@Component({
  standalone: false,
  selector: 'gh-draw-rule-details',
  templateUrl: 'draw-rule-details.component.html',
  providers: [DrawRuleDetailsFormState],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrawRuleDetailsComponent extends AbstractFormComponent<DrawRuleDetails> implements OnInit {
  ruleId: ID;
  isShowDeleted: boolean = false;
  isDeleteSmth: boolean = false;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private notifications: NotificationService,
              private navigations: BuilderNavigationService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private dialogService: DialogService,
              private business: DrawRuleDetailsBusinessService,
              public form: DrawRuleDetailsFormState) {
    super();
  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_draw_rule_create',
      [FormMode.EditMode]: 'lbl_draw_rule_edit',
      [FormMode.ViewMode]: 'lbl_draw_rule_view',
    }[this.form.mode];
  }

  get completeFlow(): ActionFlow<FormFlowState<DrawRuleDetails>> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(whenFormInvalid, this.handleErrors.bind(this)),
      execute: this.commonActions.execute(({ value }) => this.business.save(value)),
      finish: ({ value, form }) => {
        if (form.createMode) {
          this.form.resetDirty();
          this.router.switchTo(['/setup/accounting/draw-rules', value.drawRuleId]);
        } else {
          form.build(value);
        }
      },
      error: (({ error }) => {
        this.form.setStageErrors(error);
        this.notifications.processAll(error);
      }),
    });
  }

  ngOnInit(): void {
    const { details } = getRouteInitData(this.route);
    this.ruleId = details.drawRuleId;

    details.stages
      .filter((stage: DrawRuleStage) => hasDrawRuleStageCpDeleted(stage) && stage.status === 1)
      .forEach(({ name, deletedCriticalPathName }: DrawRuleStage) =>
        this.notifications.warning('msg_deleted_critical_path', {
          stageName: name,
          deletedName: deletedCriticalPathName!,
        }));

    this.form.build(details);
  }

  onMasterInfoChange(masterInfo: FormStateSnapshot<DrawRuleMasterInfo>): void {
    this.form.setMasterInfo(masterInfo);
  }

  onCurrentMemoChange(memo: string): void {
    this.form.setCurrentStageMemo(memo);
  }

  onStageSelected(stage: DrawRuleStage): void {
    this.form.setCurrentStage(stage);
  }

  onResetFocusedCell(): void {
    this.form.resetFocusedCell();
  }

  onStageAdd(): void {
    this.form.addStage();
  }

  onStageRemove(stage: DrawRuleStage): void {
    if (stage.isNew) {
      this.dialogService.confirmation(yesNoConfirmation('msg_draw_rule_stage_delete')).pipe(
        filter$(yesAnswer))
        .subscribe(() => {
          this.form.removeStage(stage);
        });
      return;
    }
    this.business.checkStageUsage(this.ruleId, stage).subscribe(({ isStageNotUsed }) => {
      if (isStageNotUsed) {
        this.dialogService.confirmation(yesNoConfirmation('msg_draw_rule_stage_delete')).pipe(
          filter$(yesAnswer))
          .subscribe(() => {
            this.form.removeStage(stage);
          });
      } else {
        this.dialogService.confirmation(yesNoConfirmation('msg_draw_rule_stage_mark_deleted')).pipe(
          filter$(yesAnswer))
          .subscribe(
            () => {
              this.isShowDeleted = true;
              this.form.markDeletedStage(stage);
            });
      }
    });
  }

  onChangeShowDeleted(value: boolean): void {
    this.isShowDeleted = value;
    this.form.showDeletedStages(value);
  }

  onStageUndelete(stage: DrawRuleStage): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_draw_rule_stage_undelete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.undeleteStage(stage);
      });
  }

  onStageSetField(stage: DrawRuleStage, name: string, value: any): void {
    this.form.stageSetField(stage, name, value);
  }

  goToAel(): void {
    this.navigations.goToAel({
      entityType: 'DRAW_RULE',
      entityId: this.form.drawRuleId,
      label: this.title,
    });
  }

  handleErrors(): void {
    if (this.form.isMasterInfoInvalid) {
      this.form.highlightMasterInfo();
      return;
    }

    if (this.form.hasInvalidStage) {
      this.notifications.error('msg_he_incorrect_data_in_tables');
      this.form.navigateToInvalidStage();
      return;
    }

    if (this.form.hasSeveralDeposits) {
      this.notifications.error('msg_rule_one_deposit');
      return;
    }

    if (this.form.isDeductDepositInvalid) {
      this.notifications.error('msg_rule_if_deposit_deduct_required');
      return;
    }

    if (this.form.isSameStageAsDepositAndDeduct) {
      this.notifications.error('msg_rule_deposit_deduct_same');
      return;
    }

    if (this.form.hasEmptyParagraph) {
      this.form.navigateToStageHavingEmptyParagraph();
      this.notifications.error('msg_rule_stage_paragraph_required');
      return;
    }

    if (this.form.isTotalPercentsInvalid) {
      this.notifications.error('msg_rule_pct_due_not_hundred');
      return;
    }

    if (this.form.isTotalBudgetInvalid) {
      this.notifications.error('msg_rule_pct_budget_not_hundred');
      return;
    }

    if (this.form.isOrderInvalid) {
      this.notifications.error('msg_draw_rule_link_cp_order_error');
      return;
    }
  }
}
